import {
  DashboardColumnStatus,
  FilterType,
  OrderByTypeValues,
  SortDirection,
  TypologyValue,
} from '@/shared';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { DateRange } from 'react-day-picker';

type TypologyTotal = {
  typology: TypologyValue;
  total: number;
};

export const FilterContext = createContext<{
  showActiveOnly: boolean;
  setShowActiveOnly: (value: boolean) => void;
  activeTypologies: string[];
  toggleTypology: (typology: TypologyValue) => void;
  filterBy?: FilterType;
  filter?: string;
  sortBy?: string;
  sortOrder: SortDirection;
  dateRange?: DateRange;
  setFilterBy: (filterBy?: FilterType) => void;
  setFilter: (filter?: string) => void;
  setSortBy: (sortBy?: string) => void;
  setDateRange: (dateRange?: DateRange) => void;
  updateNrRequestsPerColumnAndStatus: (
    column: DashboardColumnStatus,
    data: TypologyTotal[],
  ) => void;
  totalByTypology: Record<TypologyValue, number> | null;
}>({
  showActiveOnly: false,
  sortOrder: 'descending',
  setShowActiveOnly: () => {},
  activeTypologies: [],
  toggleTypology: () => {},
  setFilterBy: () => {},
  setFilter: () => {},
  setSortBy: () => {},
  setDateRange: () => {},
  updateNrRequestsPerColumnAndStatus: () => {},
  totalByTypology: null,
});

type NrStatusBycolumn = {
  [TypologyValue.Development]: {
    [DashboardColumnStatus.Concluded]: number;
    [DashboardColumnStatus.InEvaluation]: number;
    [DashboardColumnStatus.InExecution]: number;
    [DashboardColumnStatus.Submitted]: number;
  };
  [TypologyValue.Diligence]: {
    [DashboardColumnStatus.Concluded]: number;
    [DashboardColumnStatus.InEvaluation]: number;
    [DashboardColumnStatus.InExecution]: number;
    [DashboardColumnStatus.Submitted]: number;
  };
  [TypologyValue.Discovery]: {
    [DashboardColumnStatus.Concluded]: number;
    [DashboardColumnStatus.InEvaluation]: number;
    [DashboardColumnStatus.InExecution]: number;
    [DashboardColumnStatus.Submitted]: number;
  };
};

const TYPOLOGIES = [
  TypologyValue.Discovery,
  TypologyValue.Development,
  TypologyValue.Diligence,
] as const;

const STATUSES = [
  DashboardColumnStatus.Submitted,
  DashboardColumnStatus.InEvaluation,
  DashboardColumnStatus.InExecution,
  DashboardColumnStatus.Concluded,
] as const;

export function FilterProvider({ children }: PropsWithChildren) {
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [activeTypologiesRecord, setActiveTypologiesRecord] = useState<
    Record<TypologyValue, boolean>
  >({
    [TypologyValue.Development]: true,
    [TypologyValue.Diligence]: true,
    [TypologyValue.Discovery]: true,
  });

  const [filterBy, setFilterBy] = useState<FilterType>();
  const [filter, setFilter] = useState<string>();
  const [sortBy, setSortBy] = useState<string | undefined>(
    OrderByTypeValues[OrderByTypeValues.Urgency],
  );
  const [sortOrder, setSortOrder] = useState<SortDirection>('descending');

  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  const initialNrStatusByColumn: NrStatusBycolumn = {
    [TypologyValue.Development]: {
      [DashboardColumnStatus.Submitted]: 0,
      [DashboardColumnStatus.InEvaluation]: 0,
      [DashboardColumnStatus.InExecution]: 0,
      [DashboardColumnStatus.Concluded]: 0,
    },
    [TypologyValue.Diligence]: {
      [DashboardColumnStatus.Submitted]: 0,
      [DashboardColumnStatus.InEvaluation]: 0,
      [DashboardColumnStatus.InExecution]: 0,
      [DashboardColumnStatus.Concluded]: 0,
    },
    [TypologyValue.Discovery]: {
      [DashboardColumnStatus.Submitted]: 0,
      [DashboardColumnStatus.InEvaluation]: 0,
      [DashboardColumnStatus.InExecution]: 0,
      [DashboardColumnStatus.Concluded]: 0,
    },
  };

  const [nrRequestsPerColumnAndStatus, setNrRequestsPerColumnAndStatus] =
    useState<NrStatusBycolumn>(initialNrStatusByColumn);

  const updateNrRequestsPerColumnAndStatus = useCallback(
    (column: DashboardColumnStatus, data: TypologyTotal[]) => {
      setNrRequestsPerColumnAndStatus((prev) => {
        const updated: NrStatusBycolumn = { ...prev };

        data.forEach(({ typology, total }) => {
          updated[typology] = {
            ...updated[typology],
            [column]: total,
          };
        });

        return updated;
      });
    },
    [],
  );

  const totalByTypology = useMemo<Record<TypologyValue, number> | null>(() => {
    if (!nrRequestsPerColumnAndStatus) return null;

    return TYPOLOGIES.reduce(
      (acc, typology) => {
        acc[typology] = STATUSES.reduce(
          (sum, status) => sum + nrRequestsPerColumnAndStatus[typology][status],
          0,
        );
        return acc;
      },
      {} as Record<TypologyValue, number>,
    );
  }, [nrRequestsPerColumnAndStatus]);

  const activeTypologies = useMemo(() => {
    return Object.entries(activeTypologiesRecord)
      .filter(([_, isActive]) => isActive)
      .map(([key]) => key);
  }, [activeTypologiesRecord]);

  const toggleTypology = useCallback((typology: TypologyValue) => {
    setActiveTypologiesRecord((prev) => ({
      ...prev,
      [typology]: !prev[typology],
    }));
  }, []);

  const onSetFilterBy = useCallback((filterByValue?: FilterType) => {
    setFilterBy(filterByValue);
    setDateRange(undefined);
    setFilter(undefined);
  }, []);

  const onSetSortBy = useCallback((orderByValue?: string) => {
    const ascendingOrderTypes = [OrderByTypeValues[OrderByTypeValues.Typology]];

    if (orderByValue && ascendingOrderTypes.includes(orderByValue)) {
      setSortOrder('ascending');
    } else {
      setSortOrder('descending');
    }

    setSortBy(orderByValue);
  }, []);

  return (
    <FilterContext.Provider
      value={{
        showActiveOnly,
        setShowActiveOnly,
        activeTypologies,
        toggleTypology,
        filter,
        filterBy,
        sortBy: sortBy,
        sortOrder: sortOrder,
        dateRange,
        setFilterBy: onSetFilterBy,
        setFilter,
        setSortBy: onSetSortBy,
        setDateRange,
        updateNrRequestsPerColumnAndStatus,
        totalByTypology,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}
